import { merge } from '@fluentui/react';
import { useOdataPostSingleApi, useODataReadInfiniteApi, useODataReadFirstApi, } from '../odata';
import { auth } from '../util/auth';
import { endpointConversation, endpointMessage, getEndpointConversationMessage, } from './endpoint';
import { endpointVuln } from '../vulnReportApi';
import { SLOW_DATA_STALE_TIME_MSEC } from '../../constants';
/** Odata API for accessing conversations the current user has access to. */
export const useConversationListApi = (query, options) => {
    return useODataReadInfiniteApi(endpointConversation, query, auth(options));
};
/** Odata API for accessing messages from a specific conversation. */
export const useConversationMessageListApi = (conversationId, query, options = {}) => {
    return useODataReadInfiniteApi(getEndpointConversationMessage(conversationId), query, merge({}, options, {
        isAuthRequired: true,
        isVerificationRequired: true,
        enabled: !!conversationId,
        pageLength: 5,
    }), [
        //if /conversation or /message are invalid, then the messages on a given convo are now stale
        endpointMessage,
        getEndpointConversationMessage(conversationId),
        query,
    ]);
};
const firstMessageSettings = {
    orderBy: { ascending: true, key: 'createdOn' },
};
/** Odata API for accessing the first message from a specific conversation. */
export const useConversationFirstMessageApi = (conversationId) => useODataReadFirstApi(getEndpointConversationMessage(conversationId), firstMessageSettings, {
    isAuthRequired: true,
    isVerificationRequired: true,
    enabled: !!conversationId,
    useQueryOptions: {
        //This is very slow-moving data, and doesn't need refreshed quickly
        cacheTime: SLOW_DATA_STALE_TIME_MSEC,
        staleTime: SLOW_DATA_STALE_TIME_MSEC,
    },
}, [
    endpointMessage,
    getEndpointConversationMessage(conversationId),
    firstMessageSettings,
    'FirstMessage',
]);
/** Helper function for building React-query mutation APIs against `ConversationService` */
const useConversationServiceMutationApi = (serviceName, additionalEndpoints = []) => useOdataPostSingleApi(`${endpointConversation}/ConversationService.${serviceName}`, [
    ...additionalEndpoints,
    `${endpointConversation}/ConversationService.${serviceName}`,
]);
/** Shortcut API that lets you create a conversation WITH its first message */
export const useConversationCreateWithMessageApi = () => useConversationServiceMutationApi(`CreateWithMessage`, [endpointConversation]);
/** React-query mutation API for marking a batch of messages as **flagged** 🚩 */
export const useConversationFlagMutationApi = () => useConversationServiceMutationApi(`Flag`, [
    endpointMessage,
    endpointConversation,
    endpointVuln,
]);
/** React-query mutation API for marking a batch of messages as **unflagged** 🚩 */
export const useConversationUnflagMutationApi = () => useConversationServiceMutationApi(`Unflag`, [
    endpointMessage,
    endpointConversation,
    endpointVuln,
]);
/** React-query mutation API for the user to ignore future activity on a batch of conversations (unsubscribe) */
export const useConversationSubscribeMutationApi = () => useConversationServiceMutationApi(`Subscribe`, [endpointConversation, endpointVuln]);
/** React-query mutation API for the user to subscribe to future activity on a batch of conversations */
export const useConversationUnsubscribeMutationApi = () => useConversationServiceMutationApi(`Unsubscribe`, [endpointConversation, endpointVuln]);
/** Hybrid mutation api that lets you pass the state you want to set subscribe to */
export const useConversationSubscribeUnsubscribeMutationApi = () => {
    const sub = useConversationSubscribeMutationApi();
    const unsub = useConversationUnsubscribeMutationApi();
    return {
        isLoading: sub.isLoading || unsub.isLoading,
        error: sub.error || unsub.error,
        mutateAsync: (conversationId, isSubscribedNewValue) => {
            if (isSubscribedNewValue) {
                return sub.mutateAsync({ conversationIds: [conversationId] });
            }
            else {
                return unsub.mutateAsync({ conversationIds: [conversationId] });
            }
        },
    };
};
